import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { PencilSolidIcon } from "@images/svgIcons/essentials/pencilSolidIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import {
  BoxStaffPermissionsModal,
  BoxStaffPermissionsModalRef,
} from "@modules/boxStaff/components/boxStaffPermissionsModal"
import { BoxStaffRoleModal, BoxStaffRoleModalRef } from "@modules/boxStaff/components/boxStaffRoleModal"
import { BoxStaffDetailedEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { modifiedBoxStaffYupValidationSchema } from "@modules/boxStaff/formik/boxStaffFormik"
import { BoxStaffDetailedUpadteInput, BoxStaffPermissions, BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HuPermissions } from "@modules/ui/components/huPermissions"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import Line from "@modules/ui/components/line"
import { UseMutateAsyncFunction } from "@tanstack/react-query"
import { useFormik } from "formik"
import { Skeleton } from "primereact/skeleton"
import { useEffect, useRef, useState } from "react"
import { useTheme } from "styled-components"

type BoxStaffTabDayToDayProps = {
  staffDetailed?: BoxStaffDetailedEntity
  isLoading: boolean
  updateBoxStaff: UseMutateAsyncFunction<void, Error, BoxStaffDetailedUpadteInput, unknown>
  // isPending: boolean
} & React.HTMLAttributes<HTMLDivElement>

export const BoxStaffTabDayToDay: React.FC<BoxStaffTabDayToDayProps> = ({
  staffDetailed,
  isLoading,
  updateBoxStaff,
  ...props
}) => {
  const theme = useTheme()
  const toast = useToast()
  const { hideSidebar } = useSidebar()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const boxStaffPermissionsModal = useRef<BoxStaffPermissionsModalRef>(null)
  const boxStaffRoleModal = useRef<BoxStaffRoleModalRef>(null)
  const [initialNoteValue, setInitialNoteValue] = useState(staffDetailed?.internalNote || undefined)
  // PERMISSIONS - Head-coach & Coach & Financial permissions denied
  const canChangeRoleStaff = useBoxStaffPermission(BoxStaffPermissions.CHANGE_ROLE_STAFF)

  const handlePermessions = async () => {
    boxStaffPermissionsModal.current?.open()
  }

  const handleChangeRole = async () => {
    boxStaffRoleModal.current?.open()
  }

  const formik = useFormik<BoxStaffDetailedUpadteInput>({
    enableReinitialize: true,
    initialValues: { internalNote: staffDetailed?.internalNote || "", role: staffDetailed?.role || BoxStaffRole.COACH },
    validationSchema: modifiedBoxStaffYupValidationSchema(staffDetailed?.internalNote || undefined),
    onSubmit: async () => {
      setIsSubmitting(false)

      await updateBoxStaff({ internalNote: formik.values.internalNote })
        .then(() => {
          toast.show({
            severity: "success",
            detail: word("box.staff.table.dayToDay.toast.success"),
          })
        })
        .catch(() => {
          toast.show({
            severity: "error",
            detail: word("box.staff.table.dayToDay.toast.error"),
          })
        })
    },
  })

  const handleOnSubmit = async () => {
    formik.submitForm()
  }

  useEffect(() => {
    if (!isLoading && staffDetailed) {
      setInitialNoteValue(staffDetailed.internalNote || "")
      formik.setValues({ internalNote: staffDetailed.internalNote || "", role: staffDetailed.role })
    }
  }, [isLoading])

  useEffect(() => {
    if (formik.values.internalNote !== initialNoteValue) {
      setIsSubmitting(true)
    } else {
      setIsSubmitting(false)
    }
  }, [formik.values.internalNote])

  return (
    <>
      <div className="flex flex-column w-full gap-5" {...props}>
        {/* INTERNAL NOTE*/}
        <div className="flex flex-column gap-2 mt-2">
          <HUText fontStyle="TM" color={theme.mainWhite}>
            {word("box.staff.table.dayToDay.internalNote.label")}
          </HUText>
          <div className="flex flex-row gap-2 mt-2 mb-2">
            <WarningIcon color={theme.neutralColor900} width={24} height={24} className="w-1" />
            <HUText fontStyle="BS" color={theme.neutralColor400}>
              {word("box.staff.table.dayToDay.internalNote.warning")}
            </HUText>
          </div>
          {isLoading ? (
            <Skeleton height="7rem" borderRadius="8px" />
          ) : (
            <FormikInput
              flex
              formikStyle={{ flexGrow: 1, height: "8rem" }}
              type="textarea"
              placeHolder={word("box.staff.table.dayToDay.internalNote.placeholder")}
              getFieldProps={formik.getFieldProps}
              isInvalid={isFormFieldInValid("internalNote", formik)}
              name="internalNote"
              error={getFormErrorMessage("internalNote", formik)}
              disabled={!canChangeRoleStaff}
            />
          )}
        </div>
        {/* PERMISSIONS */}
        <HuPermissions
          title={word("box.staff.table.dayToDay.permissions.title")}
          role={staffDetailed?.role}
          button={
            <HUButton
              type="Right_Icon"
              size="M"
              colorType="Underline"
              text={word("box.staff.table.dayToDay.button.seeDetails")}
              onClick={() => handlePermessions()}
              className="ml-auto"
              icon={{
                iconView: <ArrowIcon color={theme.mainWhite} width={14} height={14} arrowDirection={"right"} />,
              }}
            />
          }
        />
        {/* CHANGE ROLE */}
        <div className="flex align-items-center gap-3">
          {isLoading || !staffDetailed?.role ? (
            <Skeleton height="2rem" width="10rem" borderRadius="8px" />
          ) : (
            <HUText fontStyle="LL" color={theme.mainWhite}>
              {displayRole(staffDetailed.role)}
            </HUText>
          )}
          <Line color={theme.neutralColor200} strokeWidth={1} className="flex flex-grow-1" />
          {isLoading ? (
            <Skeleton height="2rem" width="10rem" borderRadius="8px" />
          ) : staffDetailed ? (
            staffDetailed.role === BoxStaffRole.OWNER ? (
              <HUText fontStyle="LS" color={theme.neutralColor400} className="w-max">
                {word("box.staff.table.dayToDay.button.ownerCanChangeRole")}
              </HUText>
            ) : canChangeRoleStaff ? (
              <HUButton
                type="Right_Icon"
                size="XS"
                colorType="Quaternary"
                text={word("box.staff.table.dayToDay.button.changeRole")}
                onClick={() => handleChangeRole()}
                icon={{
                  iconView: (
                    <PencilSolidIcon
                      color={canChangeRoleStaff ? theme.mainWhite : theme.neutralColor200}
                      width={14}
                      height={14}
                    />
                  ),
                }}
                disabled={isLoading || !canChangeRoleStaff}
              />
            ) : null
          ) : null}
        </div>
        {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
        {canChangeRoleStaff && (
          // BUTTON
          <div className="mb-4 flex flex-row gap-2 align-items-center justify-content-center">
            <HUButton
              type="Left_Icon"
              size="M"
              colorType="Quaternary"
              text={word("global.cancel")}
              onClick={() => hideSidebar()}
              className="w-full"
            />
            <HUButton
              type="Right_Icon"
              size="M"
              colorType="Primary"
              text={word("global.save")}
              onClick={() => isSubmitting && handleOnSubmit()}
              disabled={!isSubmitting || isLoading}
              className="w-full"
            />
          </div>
        )}
      </div>
      <BoxStaffPermissionsModal ref={boxStaffPermissionsModal} />
      {staffDetailed?.role && <BoxStaffRoleModal staffDetailed={staffDetailed} ref={boxStaffRoleModal} />}
    </>
  )
}
